import { Inject, Injectable } from '@angular/core';
import {
  MeasureAggregateOperator,
  MultiLocationAnalyticsService,
  Order,
  QueryMetricsRequest,
} from '@vendasta/multi-location-analytics';
import { Observable } from 'rxjs';
import { map, publishReplay, refCount, switchMap } from 'rxjs/operators';
import { extractMetricsFromResponse } from '../core/multi-location-analytics';

export interface FormatSignOptions {
  fixed?: boolean;
}

export class Currency {
  constructor(
    public readonly code: string,
    public readonly sign: string,
    public readonly penniesToDollars: (value: number) => number, //we can't actually use this because we store the values corrupted elsewhere, but it's still good info to have
    public readonly dollarsToFixed: (value: number) => string,
  ) {}

  displayValue(pennies: number): number {
    //     return Number(this.dollarsToFixed(this.penniesToDollars(pennies)));
    return Number(this.dollarsToFixed(pennies / 100));
  }

  public formatSign(dollars: number | string, options: FormatSignOptions = {}): string {
    options = { fixed: true, ...options }; //last value wins
    let numberSign = '';
    if (Number(dollars) < 0) {
      numberSign = '-';
      if (typeof dollars === 'number') {
        dollars = Math.abs(dollars);
      } else {
        dollars.replace('-', '');
      }
    }
    if (!options.fixed) {
      return `${numberSign}${this.sign}${dollars}`;
    }
    return `${numberSign}${this.sign}${this.dollarsToFixed(Number(dollars))}`;
  }

  public formatUnit(dollars: number): string {
    return `${this.code}${this.dollarsToFixed(dollars)}`;
  }

  public kFormat(dollars: number, options: FormatSignOptions = {}): string {
    if (Math.abs(dollars) < 1000) {
      return this.formatSign(dollars, { fixed: false, ...options });
    }
    dollars = (Math.sign(dollars) * Math.round(Math.abs(dollars) / 100)) / 10; //js Math.round does round-up, not away-from-zero
    return this.formatSign(dollars, { fixed: false }) + 'K';
  }
}

// extracted from unicode.org/cldr version 36, for all currencies that existed in vendor-set MSRP's as of 2019-11-28
export const CURRENCIES: { [key: string]: Currency } = {
  ISK: new Currency(
    'ISK',
    'kr',
    (pennies) => pennies,
    (dollars) => dollars.toFixed(0),
  ),
  JPY: new Currency(
    'JPY',
    '¥',
    (pennies) => pennies,
    (dollars) => dollars.toFixed(0),
  ),
  AUD: new Currency(
    'AUD',
    '$',
    (pennies) => pennies / 100,
    (dollars) => dollars.toFixed(2),
  ),
  AWG: new Currency(
    'AWG',
    'KM',
    (pennies) => pennies / 100,
    (dollars) => dollars.toFixed(2),
  ),
  BRL: new Currency(
    'BRL',
    'R$',
    (pennies) => pennies / 100,
    (dollars) => dollars.toFixed(2),
  ),
  CHF: new Currency(
    'CHF',
    '$',
    (pennies) => pennies / 100,
    (dollars) => dollars.toFixed(2),
  ),
  CZK: new Currency(
    'CZK',
    'Kč',
    (pennies) => pennies / 100,
    (dollars) => dollars.toFixed(2),
  ),
  GBP: new Currency(
    'GBP',
    '£',
    (pennies) => pennies / 100,
    (dollars) => dollars.toFixed(2),
  ),
  NZD: new Currency(
    'NZD',
    '$',
    (pennies) => pennies / 100,
    (dollars) => dollars.toFixed(2),
  ),
  SGD: new Currency(
    'SGD',
    '$',
    (pennies) => pennies / 100,
    (dollars) => dollars.toFixed(2),
  ),
  USD: new Currency(
    'USD',
    '$',
    (pennies) => pennies / 100,
    (dollars) => dollars.toFixed(2),
  ),
  CAD: new Currency(
    'CAD',
    '$',
    (pennies) => pennies / 100,
    (dollars) => dollars.toFixed(2),
  ),
  EUR: new Currency(
    'EUR',
    '€',
    (pennies) => pennies / 100,
    (dollars) => dollars.toFixed(2),
  ),
  ZAR: new Currency(
    'ZAR',
    'R',
    (pennies) => pennies / 100,
    (dollars) => dollars.toFixed(2),
  ),
  IQD: new Currency(
    'IQD',
    'kr',
    (pennies) => pennies / 1000,
    (dollars) => dollars.toFixed(3),
  ),
  TND: new Currency(
    'TND',
    'T$',
    (pennies) => pennies / 1000,
    (dollars) => dollars.toFixed(3),
  ),
  KWD: new Currency(
    'KWD',
    '$',
    (pennies) => pennies / 1000,
    (dollars) => dollars.toFixed(3),
  ),
  JOD: new Currency(
    'JOD',
    '¥',
    (pennies) => pennies / 1000,
    (dollars) => dollars.toFixed(3),
  ),
  BHD: new Currency(
    'BHD',
    '$',
    (pennies) => pennies / 1000,
    (dollars) => dollars.toFixed(3),
  ),
  CLF: new Currency(
    'CLF',
    '$',
    (pennies) => pennies / 10000,
    (dollars) => dollars.toFixed(4),
  ),
};

export const PARTNER_CURRENCY_MAP: { [key: string]: Currency } = {
  TRUD: CURRENCIES.ZAR,
  SORD: CURRENCIES.USD,
};

export const DEFAULT_CURRENCY = CURRENCIES.USD;

export type CurrencyFormatter = (value: number) => string;
@Injectable({ providedIn: 'root' })
export class CurrencyService {
  currency$: Observable<Currency>;
  kFormat$: Observable<CurrencyFormatter>;

  constructor(
    @Inject('PARTNER_ID') private partner$: Observable<string>,
    private multiLocationAnalyticsService: MultiLocationAnalyticsService,
  ) {
    this.currency$ = this.partner$.pipe(
      switchMap((pid) => {
        const request = {
          partnerId: pid,
          metricName: 'saas_display_currency',
          resourceIds: [
            {
              marketId: {
                marketIds: ['__ALL__'],
              },
            },
          ],
          measures: [
            {
              aggregate: {
                measure: 'currency',
                aggOp: MeasureAggregateOperator.COUNT,
                alias: 'count',
              },
            },
          ],
          groupBy: {
            dimension: [
              {
                dimension: 'currency',
              },
            ],
          },
          limit: 1,
          orderBy: {
            orderBy: [
              {
                column: 'count',
                order: Order.ORDER_DESC,
              },
            ],
          },
        };
        return this.multiLocationAnalyticsService.queryMetrics(request as QueryMetricsRequest).pipe(
          map((resp) => {
            const metrics = extractMetricsFromResponse(resp);
            if (!metrics.length || !metrics[0] || !metrics[0].dimension || PARTNER_CURRENCY_MAP[pid]) {
              return PARTNER_CURRENCY_MAP[pid] || DEFAULT_CURRENCY;
            }
            const currency = metrics[0].dimension;
            return CURRENCIES[currency] || DEFAULT_CURRENCY;
          }),
        );
      }),
      publishReplay(1),
      refCount(),
    );
    this.kFormat$ = this.currency$.pipe(map((currency) => (value) => currency.kFormat(value)));
  }
}
